<template>
	<div class="height-100 display-flex flex-column">
		<div v-if="isShowWidgetPreview" class="preview-container pt-9 flex-stretch-to-scroll">
			<WidgetForm
				ref="preview_widget_layout"
				class="widget-form-container widget-preview-mode"
				:testResponse="editedTestResponse"
				:isEndRun="isEndRunWidget"
				:stickyFooter="isTileEditor"
			/>
			<WidgetTestFooter @hide-widget="hideWidget" bottomBar />
		</div>
		<div v-else-if="testWidgetMode" class="widget-test-preview-container height-100">
			<WidgetEditHeader @edit-structure="hideWidget" :hasEditPermissions="hasEditPermissions" noPreview />
			<div class="test-widget-mode-form-container flex-stretch-to-scroll display-flex flex-column flex-justify-space-between">
				<WidgetForm
					ref="widget_layout"
					class="widget-form-container widget-test-mode overflow-y-auto"
					:testResponse="editedTestResponse"
					:isEndRun="isEndRunWidget"
					:stickyFooter="isTileEditor"
				/>
				<WidgetTestFooter @hide-widget="hideWidget" />
			</div>
		</div>
		<div v-else class="block-editor-container display-flex flex-column height-100">
			<perfect-scrollbar ref="scroll" class="block-editor-content-section is-widget height-100">
				<transition name="fromLeft" mode="out-in">
					<div v-if="isReturnToTestedWidget" class="display-flex flex-column width-100 height-100 tested-widget-wrapper">
						<WidgetEditHeader :hasEditPermissions="hasEditPermissions" @edit-structure="hideWidget" @show-preview="onShowPreviewClick" />
						<WidgetForm
							ref="tested_widget_layout"
							class="height-100 widget-form-container widget-block-editor-mode flex-stretch-to-scroll"
							:testResponse="testResponse"
							:testResponseNotFlattened="testResponseNotFlattened"
							:isEndRun="isEndRunWidget"
							:stickyFooter="isTileEditor"
						/>
					</div>
					<div v-else class="block-editor">
						<transition name="fromTopLong" mode="out-in">
							<v-row no-gutters class="height-100" style="position: relative">
								<v-col cols="12" class="text-left height-100">
									<WidgetEditor
										:hasEditPermissions="hasEditPermissions"
										:testResponse="testResponse"
										@update-value="handleValueUpdate"
										@show-preview="setIsShowWidgetPreview(true)"
									/>
								</v-col>
							</v-row>
						</transition>
					</div>
				</transition>
			</perfect-scrollbar>

			<WidgetTestFooter v-if="showTestModeBottomBar" bottomBar @hide-widget="hideWidget" />
			<BlockEditorBottomBar
				v-else
				btnTextInput="Run to create sample data"
				:loadingTestAndContinue="loadingTestAndContinue"
				:isTestingWidget.sync="isTestingWidget"
				:showTestResultModal.sync="isShowTestResultModal"
				:btnDisabled="btnDisabled"
				:testResponse="testResponse"
				:testResultStatus="testResultStatus"
				:showParams="false"
				:hasEditPermissions="hasEditPermissions"
				:btnFuncLoading="isBtnFuncLoading"
				@btnFunc="btnFunc"
				@skipTest="skipTestNode"
			/>

			<div
				class="test-modal"
				:class="{
					'is-visible': isShowTestResultModal,
					'is-wide': isWideWidget && isTestingWidget,
					'narrow-widget-max-width': showNarrowTestResultModal,
				}"
				v-if="showTestResultPopUp"
			>
				<TestResultModal v-if="isTestingWidget && !isShowWidgetTestPreview" :isError="isTestError" :isTested="false">
					<div class="description flex-centerize">
						Use the widget once to test it, <br />
						Fill in the data, and click "Done".
					</div>
					<WidgetForm ref="widget_layout" :isEndRun="isEndRunWidget" />
				</TestResultModal>
				<TestResultModal v-else :isError="isTestError" @onClose="hideTestCard">
					<TestResultsDataProvider :showTestResultModal.sync="isShowTestResultModal" @test-again="applyTestAgain" />
				</TestResultModal>
				<div v-if="loadingTestAndContinue">
					<Loader />
				</div>
			</div>
			<div class="editor-overlay" :class="{ show: isShowTestResultModal }" @click="hideTestCard">
				<Loader v-if="loadingTestAndContinue" />
			</div>
		</div>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { mapActions, mapGetters } from 'vuex';

/* Components */

import BlockEditorBottomBar from './BlockEditorBottomBar';
import TestResultModal from './TestResultModal';
import TestResultsDataProvider from './helpers/TestResultsDataProvider';
import api from '@/api';
import WidgetEditHeader from '@/components/BlockEditorWidget/WidgetEditHeader';
import WidgetEditor from '@/components/BlockEditorWidget/WidgetEditor';
import WidgetTestFooter from '@/components/BlockEditorWidget/WidgetTestFooter';
import widgetFieldTypes from '@/components/BlockEditorWidget/widgetFieldTypes';
import WidgetForm from '@/components/WidgetForm';
import { getAction } from '@/store/modules/actions/getters';
import { getContext } from '@/store/modules/contexts/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { getNodeData, testStep as nodeTestStep, skipTest as nodeSkipTest } from '@/store/modules/nodes/actions';
import {
	getNodeTestResult,
	getNode,
	loadingTestAndContinue as loadingTestAndContinueGetter,
	previousStepsData,
	getNodeOutputLabelsByNodeId,
	useIsRootNode,
} from '@/store/modules/nodes/getters';
import { testWidgetStep, setSideBarState, resetWidgetFieldTestStatus } from '@/store/modules/selectedNode/actions';
import { flatPreviousSteps, isEndRunWidget as isEndRunWidgetGetter } from '@/store/modules/selectedNode/getters';
import {
	replaceInsertDataStringDictionary,
	editWorkflow,
	rootNode,
	element,
	isNativeElement,
	isOfTypeFeature,
	isOfTypeTool,
	selectedWorkflowId,
} from '@/store/modules/selectedWorkflow/getters';
import {
	configurationsMappingBlocksUUIDs,
	getConfigurationAndInterfaceIdByFieldUUID,
} from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { setEditField } from '@/store/modules/widgetEditor/actions';
import { editField, widget, widgetFields, widgetMetadata, isLoadingWidgetFields } from '@/store/modules/widgetEditor/getters';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import WorkflowService from '@/utils/WorkflowService';
import ErrorHandlingService from '@/utils/error-handling-service';
import { flattenPreserveArrays } from '@/utils/helpers';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';

export default {
	name: 'WidgetBlockEditor',
	components: {
		WidgetEditHeader,
		TestResultsDataProvider,
		WidgetEditor,
		BlockEditorBottomBar,
		WidgetForm,
		TestResultModal,
		WidgetTestFooter,
	},
	props: {
		nodeID: { type: [Number, String] },
	},
	data() {
		return {
			isReturnToTestedWidget: false,
			isShowTestResultModal: false,
			isTestingWidget: false,
			isShowAnimation: false,
			isShowWidgetTestPreview: false,
			isShowWidgetPreview: false,
			isTestedDataChanged: false,
			isBtnFuncLoading: false,
			isChangeAction: false,
			hasTestParams: false,
			accordionMenuItem: null,
			widgetTestValues: {},
			widgetTestValuesLabels: {},
			timer: null,
		};
	},
	async mounted() {
		if (this.isSuccessResultStatus) {
			this.setTestedWidgetState();
		}
	},
	watch: {
		reSetTestedWidget(newV) {
			if (newV) {
				this.setTestedWidgetState();
			}
		},
		editFieldPrefill(newV, oldV) {
			if (oldV !== undefined && this.editField) {
				// when first selecting 'editField', oldV returns undefined
				delete this.testResponse[`values.${this.editField.id}`];
				delete this.widgetTestValues[this.editField.id];
				this.resetWidgetFieldTestStatusDebounced(this.editField.id);
			}
		},
	},
	computed: {
		...mapGetters([
			isLoadingWidgetFields.fullPath,
			editField.fullPath,
			widgetMetadata.fullPath,
			isEndRunWidgetGetter.fullPath,
			widget.fullPath,
			widgetFields.fullPath,
			getNodeTestResult.fullPath,
			getNode.fullPath,
			rootNode.fullPath,

			isNativeElement.fullPath,
			loadingTestAndContinueGetter.fullPath,
			previousStepsData.fullPath,
			getAction.fullPath,
			getConfigurationFieldResponse.fullPath,
			isConfigurationsOn.fullPath,
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
			getNodeOutputLabelsByNodeId.fullPath,
			isOfTypeFeature.fullPath,
			isOfTypeTool.fullPath,
			selectedWorkflowId.fullPath,
			canEditWorkflow.fullPath,
		]),
		...mapGettersShorthand({
			flatPreviousSteps,
			element,
			getContext,
			useIsRootNode,
			editWorkflow,
		}),
		reSetTestedWidget() {
			return this.isReturnToTestedWidget && !this[isLoadingWidgetFields.fullPath];
		},
		editFieldPrefill() {
			return this.editField?.metadata?.options?.prefill?.value;
		},
		editField() {
			return this[editField.fullPath];
		},
		editedTestResponse() {
			return isEmpty(this.widgetTestValues) ? this.testResponse : this.widgetTestValues;
		},
		showTestModeBottomBar() {
			return this.isTestedDataChanged && this.isReturnToTestedWidget;
		},
		testWidgetMode() {
			return this.isShowWidgetTestPreview; // && this.isWidget;
		},
		isSuccessResultStatus() {
			return this.testResultStatus === TEST_RESULT_STATUSES.SUCCESS;
		},
		showTestResultPopUp() {
			return !this.loadingTestAndContinue && this.currentEditNode && !this.hasTestParams && this.isShowTestResultModal;
		},
		showNarrowTestResultModal() {
			return !this.isTestingWidget || !this.isWideWidget;
		},
		isWideWidget() {
			return this.widgetOptions.is_wide?.active;
		},
		widgetOptions() {
			return this[widgetMetadata.fullPath]?.options || {};
		},
		isEndRunWidget() {
			return this[isEndRunWidgetGetter.fullPath];
		},
		loadingTestAndContinue() {
			return this[loadingTestAndContinueGetter.fullPath];
		},
		currentEditNode() {
			return this[getNode.fullPath](this.nodeID);
		},
		editNodeTestResponse() {
			const { id: nodeId } = this.currentEditNode;
			const testResponseOutputLabels = this[getNodeOutputLabelsByNodeId.fullPath](nodeId);
			const testResult = this[getNodeTestResult.fullPath](nodeId);
			return WorkflowNodeService.replaceKeysWithOutputLabels(testResult, testResponseOutputLabels);
		},
		testResponseNotFlattened() {
			if (this.currentEditNode) {
				return this.editNodeTestResponse;
			}
			return {};
		},
		testResponse() {
			if (this.currentEditNode) {
				return flattenPreserveArrays(this.editNodeTestResponse);
			}
			return {};
		},
		testResultStatus() {
			const node = this.getSourceOfRelevantNode();
			return node ? node.test_result_status : null;
		},
		serviceVersion() {
			return this.currentEditNode?.service_version;
		},
		hideTestBtn() {
			return !this.hasEditPermissions;
		},
		hasEditPermissions() {
			return this[canEditWorkflow.fullPath](this[selectedWorkflowId.fullPath]);
		},
		btnDisabled() {
			if (!this.hasEditPermissions) {
				return true;
			}
			return !this.widgetFieldsFilledProperly;
		},
		isTestError() {
			return (
				this.testResponse.hasOwnProperty('error.message') ||
				this.testResponse.hasOwnProperty('error') ||
				this.testResponse.hasOwnProperty('Error') ||
				this.testResultStatus === TEST_RESULT_STATUSES.ERROR
			); // TODO change this to field 'error'
		},
		widgetFieldsFilledProperly() {
			return this[widgetFields.fullPath].every((widgetField) => {
				const fieldConf = widgetFieldTypes[widgetField.field_type];
				return fieldConf.properlyFilledValidator ? fieldConf.properlyFilledValidator(widgetField, this.$store) : true;
			});
		},
		isTileEditor() {
			return WorkflowService.isOfTypeTile(this.editWorkflow);
		},
	},
	methods: {
		...mapActions([
			resetWidgetFieldTestStatus.fullPath,
			setEditField.fullPath,
			testWidgetStep.fullPath,
			getNodeData.fullPath,
			nodeTestStep.fullPath,
			nodeSkipTest.fullPath,
			setSideBarState.fullPath,
		]),

		resetWidgetFieldTestStatusDebounced: debounce(function (id) {
			this[resetWidgetFieldTestStatus.fullPath](id);
		}, 500),
		async onShowPreviewClick() {
			await this.setIsShowWidgetPreview(true);
			await this.setIsReturnToTestedWidget(false);
		},
		setWidget(refName) {
			const previousSteps = this.flatPreviousSteps;
			const replaceInsertDataStringDict = this[replaceInsertDataStringDictionary.fullPath];
			const configurationsMappingBlocksUUIDsValues = this[configurationsMappingBlocksUUIDs.fullPath];
			this.$nextTick(() => {
				this.$refs[refName]?.setWidget({
					widget: Object.assign(
						{},
						{ ...this[widget.fullPath] },
						{ previousSteps, replaceInsertDataStringDict, configurationsMappingBlocksUUIDs: configurationsMappingBlocksUUIDsValues }
					),
					callbacks: {
						valueUpdate: this.handleValueUpdate,
						labelUpdate: this.handleLabelUpdate,
						doneClicked: this.testWidgetData,
						cancelClicked: this.hideWidget,
					},
					api: { queryAC: api.queryAC, getWidgetFieldUploadUrl: api.getWidgetFieldUploadUrl },
					isEndRun: true,
				});
			});
		},
		async setTestedWidgetState() {
			await this.setIsReturnToTestedWidget(true);
			this.setWidget('tested_widget_layout');
		},
		hideTestCard() {
			this.isShowTestResultModal = false;
		},
		async testWidgetData() {
			await this.setIsShowWidgetPreview(false);
			this.isShowWidgetTestPreview = false;
			const payload = { values: this.widgetTestValues, labels: this.widgetTestValuesLabels };
			await this.testAndContinue(payload);
			await this[getNodeData.fullPath]({ id: this.nodeID, forceGet: true }); // TODO: ret.response_labels in testAndContinue is the reason we load all the editNode from the start, replace it locally?
			this.isTestingWidget = false;
			this.isTestedDataChanged = false;
			this.widgetTestValues = {};
			this.widgetTestValuesLabels = {};
			this.setTestedWidgetState();
		},
		async hideWidget() {
			if (!this.isTestingWidget && !this.isShowWidgetPreview) {
				await this.setIsReturnToTestedWidget(false);
				return;
			}
			this.isTestingWidget = false;
			this.isShowTestResultModal = false;
			await this.setIsShowWidgetPreview(false);
			await this.setIsReturnToTestedWidget(false);
			this.isShowWidgetTestPreview = false;
			this.setTestedWidgetState();
			await this.setIsReturnToTestedWidget(false);
		},
		applyTest() {
			this.testWidget();
		},
		applyTestAgain(payload) {
			this.applyTest(payload);
		},
		async testWidget() {
			this[setEditField.fullPath](null);
			this.isTestingWidget = true;
			this.widgetTestValuesLabels = {};
			this.isShowWidgetTestPreview = true;
			this.isShowTestResultModal = true;
			/**
			 * handle context mappedFields from url manually
			 */
			this.setWidget('widget_layout');
		},
		handleValueUpdate(payload, fromInitialize) {
			this.$set(this.widgetTestValues, payload.id, payload.value);
			if (!fromInitialize) {
				this.isTestedDataChanged = true;
			}
		},
		handleLabelUpdate(payload) {
			this.$set(this.widgetTestValuesLabels, payload.id, payload.label);
		},
		async skipTestNode() {
			try {
				await this[nodeSkipTest.fullPath]({ id: this.nodeID });
			} catch (err) {
				this.$toast.error(ErrorHandlingService.extractErrorMessage(err) || 'Error trying to skip node');
			}
		},
		async setIsReturnToTestedWidget(newV) {
			this.isReturnToTestedWidget = newV;
			await this[setSideBarState.fullPath]({ sideBarState: newV ? 'tested-widget' : 'widget' });
		},
		async setIsShowWidgetPreview(newV) {
			this.isShowWidgetPreview = newV;
			await this[setSideBarState.fullPath]({ sideBarState: newV ? 'force-full-width' : 'widget' });
			if (newV) {
				this.setWidget('preview_widget_layout');
			}
		},
		async testAndContinue(testValues) {
			setTimeout(() => {
				this.isShowTestResultModal = true;
			}, 200);

			await this[testWidgetStep.fullPath](testValues);
		},
		btnFunc() {
			this.testWidget();
		},
		getSourceOfRelevantNode() {
			// we might want to do some validations on the node object came from server and not the one in the local state
			if (this.currentEditNode) {
				const { id: nodeId } = this.currentEditNode;
				const node = this[getNode.fullPath](nodeId);
				return node;
			}
			return null;
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/components/WidgetForm/widget-form-mixins.scss';
@import './block-editor.scss';

.widget-test-preview-container {
	top: 0px;
	left: 0px;
	right: 0px;
	bottom: 0px;
	z-index: $overlay-z-index;
	position: absolute;
	background: var(--faded-background);
}
.tested-widget-wrapper {
	background: var(--faded-background);
}
.preview-container {
	position: absolute;
	top: 0px;
	bottom: 0px;
	left: 0px;
	right: 0px;
	background: var(--faded-background);
}

.test-widget-mode-form-container {
	height: calc(100% - 66px);
}
.widget-form-container {
	height: 90%;
	padding-bottom: 60px;

	overflow-x: hidden;

	&.widget-test-mode {
		margin: 20px 0 -20px 0;

		::v-deep(.widget-form-element) {
			@include widget-form-desktop-layout(400px);
		}
	}

	&.widget-preview-mode {
		::v-deep(.widget-form-element) {
			@include widget-form-desktop-layout(480px);

			max-height: calc(100vh - 400px);
			overflow: auto;
		}
	}
	&.widget-block-editor-mode {
		::v-deep(.widget-form-element) {
			@include widget-form-desktop-layout(350px);

			max-width: var(--widget-form-width) !important;
		}
	}
}
</style>
